import React, { useState, useEffect, useRef } from "react";
import { API_ENDPOINT } from "../../../utilities/constant";
import axios from "axios";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { toastError } from "../../../utilities/utils";
import Image from "next/image";
import useModalScrollLock from "../../../hooks/useModalScrollLock";
import { createPortal } from "react-dom";

const subscriptionCharges = [
  {
    keywords: 50,
    planName: "Starter",
  },
  {
    keywords: 100,
    planName: "Pro",
  },
  {
    keywords: 500,
    planName: "Growth",
  },
  {
    keywords: 1000,
    planName: "Agency",
  },
];

function ManualUpgradePopUp({ setIsPopup, isPopup }) {
  const router = useRouter();
  const token = Cookies.get("token");
  const [dropdown, setDropdown] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState({
    planName: "Pro",
    price: subscriptionCharges[1]?.price || "£9.99",
    keyword: subscriptionCharges[1]?.keywords || 100,
  });
  
  // Store scroll position when popup opens
  const scrollPositionRef = useRef(0);
  
  // Apply scroll lock when popup is open
  useModalScrollLock(isPopup);
  
  // State for portal mounting
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Save scroll position when opening popup and restore when closing
  useEffect(() => {
    if (isPopup) {
      // Store current scroll position when opening
      scrollPositionRef.current = window.scrollY;
    }
  }, [isPopup]);

  // Prevent touch events on the background when popup is open
  useEffect(() => {
    const preventTouchOnBody = (e) => {
      if (e.target === document.body) {
        e.preventDefault();
      }
    };

    if (isPopup) {
      document.addEventListener('touchmove', preventTouchOnBody, { passive: false });
    }
    
    return () => {
      document.removeEventListener('touchmove', preventTouchOnBody);
    };
  }, [isPopup]);

  const handleChange = (selectedKeywords) => {
    const subscription = subscriptionCharges.find(
      (subscription) => subscription.keywords === selectedKeywords
    );

    setSelectedSubscription(subscription);
    setDropdown(!dropdown);
  };

  const manageSubscription = async (e) => {
    e.preventDefault();
    try {
      const url = API_ENDPOINT + "/manage-billings/";
      const response = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      console.log(response.data);
      const billingUrl = response.data.data.url;
      router.push(billingUrl);
    } catch (err) {
      console.log(err, "=====errr");
      toastError("Something went wrong please contact admin");
    }
  };

  // Close popup handler with scroll position maintenance
  const handleClosePopup = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling
    }
    
    setIsPopup(false);
    
    // Restore scroll position with a small delay to ensure DOM updates
    setTimeout(() => {
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: 'instant' // Use instant to avoid animation
      });
    }, 10);
  };

  const popupContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 sm:p-0" 
         style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
         onClick={(e) => {
           if (e.target === e.currentTarget) {
             handleClosePopup(e);
           }
         }}
         onTouchStart={(e) => {
           if (e.target === e.currentTarget) {
             handleClosePopup(e);
           }
         }}>
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-2xl overflow-hidden">
        {/* Close button */}
        <div
          className="absolute top-2 right-2 sm:top-4 sm:right-4 cursor-pointer z-50"
          onClick={handleClosePopup}
          style={{
            display: "flex",
            width: "32px",
            height: "32px",
            padding: "4px",
            borderRadius: "8px",
            background: "#FFF",
            boxShadow: "0px 2px 10px 0px rgba(20, 64, 175, 0.15)",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease-in-out",
            border: "1px solid rgba(20, 64, 175, 0.1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(1px)";
            e.currentTarget.style.boxShadow =
              "0px 1px 5px rgba(20, 64, 175, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0px 2px 10px rgba(20, 64, 175, 0.15)";
          }}
        >
          <span
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#667085",
              lineHeight: "20px",
            }}
          >
            &times;
          </span>
        </div>

        {/* Header with image */}
        <div className="h-[140px] sm:h-[180px] bg-gradient-to-r from-[#D9F4E5] to-[#E3FFD1] flex items-center justify-center relative">
          <Image
            alt="upgrade image"
            className="h-[150px] sm:h-[200px] w-auto object-contain mt-2"
            width={300}
            height={300}
            src="/images/upgrade_img.webp"
          />
        </div>

        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-5 sm:pb-6">
          {/* Title with decorative elements */}
          <div className="text-center relative">
            <div className="absolute left-0 right-0 -top-1 flex justify-center">
              <div className="h-0.5 w-16 bg-gradient-to-r from-[#179B81] to-[#00E5B9] rounded-full"></div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mt-3">
              Upgrade Your Experience
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Choose a plan that fits your needs
            </p>
          </div>

          {/* Plan selection card */}
          <div className="mt-4 sm:mt-5 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-100">
            {/* Plan selector */}
            <div className="relative">
              <div
                onClick={() => setDropdown(!dropdown)}
                className="relative flex items-center justify-between bg-white text-gray-800 border border-gray-200 rounded-md py-2.5 sm:py-3 px-3 sm:px-4 cursor-pointer w-full mx-auto transition-all hover:border-[#179B81] group shadow-sm"
              >
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-[#179B81] mr-2.5"></div>
                  <span className="font-medium text-base">
                    {selectedSubscription?.planName} Plan
                  </span>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  className={`transition-transform duration-200 ${
                    dropdown ? "rotate-180" : ""
                  } text-gray-500`}
                >
                  <path
                    d="M7 10L12 15L17 10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {dropdown && (
                <>
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDropdown(false);
                    }}
                    className="fixed inset-0 z-20"
                  />
                  <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden">
                    {subscriptionCharges.map((item, index) => (
                      <div
                        key={index}
                        onClick={() => handleChange(item.keywords)}
                        className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors flex items-center"
                      >
                        <div
                          className={`w-3 h-3 rounded-full mr-2.5 ${
                            selectedSubscription?.planName === item.planName
                              ? "bg-[#179B81]"
                              : "bg-gray-300"
                          }`}
                        ></div>
                        <span
                          className={`text-base ${
                            selectedSubscription?.planName === item.planName
                              ? "font-semibold text-[#179B81]"
                              : "text-gray-700"
                          }`}
                        >
                          {item.planName} Plan
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Plan features content based on selection */}
            <div className="mt-3 sm:mt-4 pl-3 sm:pl-5">
              {selectedSubscription.planName === "Starter" && (
                <>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      50 Keywords included
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      5 projects
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      AI powered insights
                    </span>
                  </div>
                  <div className="flex items-start group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-600 italic leading-5 group-hover:text-gray-900 transition-colors">
                      and more...
                    </span>
                  </div>
                </>
              )}
              
              {selectedSubscription.planName === "Pro" && (
                <>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      100 Keywords included
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      20 projects
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      Live Sharing
                    </span>
                  </div>
                  <div className="flex items-start group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-600 italic leading-5 group-hover:text-gray-900 transition-colors">
                      Reports and more...
                    </span>
                  </div>
                </>
              )}
              
              {selectedSubscription.planName === "Growth" && (
                <>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      500 Keywords included
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      Unlimited Projects
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                       White Label Reports
                    </span>
                  </div>
                  <div className="flex items-start group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-600 italic leading-5 group-hover:text-gray-900 transition-colors">
                      Live Sharing and more...
                    </span>
                  </div>
                </>
              )}
              
              {selectedSubscription.planName === "Agency" && (
                <>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      1000 Keywords included
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      Unlimited projects
                    </span>
                  </div>
                  <div className="flex items-start mb-3 group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 leading-5 group-hover:text-gray-900 transition-colors">
                      Live Sharing
                    </span>
                  </div>
                  <div className="flex items-start group">
                    <div className="h-5 w-5 rounded-full bg-gradient-to-r from-[#06AD63] to-[#179B81] mr-3 flex-shrink-0 flex items-center justify-center shadow-sm transform transition-all duration-200 group-hover:shadow-md group-hover:scale-105">
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-600 italic leading-5 group-hover:text-gray-900 transition-colors">
                    Advanced White Label Reports and more...
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={(e) => manageSubscription(e)}
            className="mt-4 sm:mt-5 w-full bg-gradient-to-r from-[#06AD63] to-[#05965A] hover:from-[#059C59] hover:to-[#048550] text-white py-3 sm:py-3.5 rounded-lg transition-all shadow-sm flex items-center justify-center group"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
              className="mr-2 group-hover:translate-y-[-2px] transition-transform"
            >
              <path
                d="M3.74967 13.75C2.49967 14.8 2.08301 17.9167 2.08301 17.9167C2.08301 17.9167 5.19967 17.5 6.24967 16.25C6.84134 15.55 6.83301 14.475 6.17467 13.825C5.85076 13.5159 5.42409 13.3372 4.97653 13.3234C4.52897 13.3096 4.09207 13.4615 3.74967 13.75Z"
                fill="white"
              />
              <path
                d="M9.99967 12.5L7.49967 10C7.94313 8.84957 8.50151 7.74676 9.16634 6.70838C10.1373 5.15587 11.4894 3.87758 13.0938 2.99512C14.6983 2.11266 16.5019 1.65535 18.333 1.66671C18.333 3.93338 17.683 7.91671 13.333 10.8334C12.2804 11.499 11.1637 12.0573 9.99967 12.5Z"
                fill="white"
              />
              <path
                d="M7.49967 10H3.33301C3.33301 10 3.79134 7.47504 4.99967 6.66671C6.34967 5.76671 9.16634 6.66671 9.16634 6.66671"
                fill="white"
              />
              <path
                d="M9.99967 12.5V16.6667C9.99967 16.6667 12.5247 16.2084 13.333 15C14.233 13.65 13.333 10.8334 13.333 10.8334"
                fill="white"
              />
            </svg>
            <span className="text-base font-medium">Upgrade to {selectedSubscription.planName}</span>
          </button>

          <p className="text-gray-400 text-xs text-center mt-3">
            Unlock more tools, insights & growth. Upgrade today.
          </p>
        </div>
      </div>
    </div>
  );

  // Return null if component is not mounted yet or use portal to render at top level
  return mounted && typeof window !== 'undefined' 
    ? createPortal(popupContent, document.body) 
    : null;
}

export default ManualUpgradePopUp;
